/**
 * The screen a Google account that is not the owner's account gets.
 *
 * It is signed in, it is valid, and it belongs to nothing - which is indistinguishable, from
 * the browser, from the first person ever to arrive. The difference is that Brunel already
 * has an owner, and offering this session the "create your organization" screen would make a
 * second one beside him.
 *
 * So this offers nothing to accept. It says which account is signed in, says plainly what
 * has happened, and gives the two ways out: back to the sign-in he knows, or out entirely.
 * It creates nothing, links nothing, and asks the identity service for nothing.
 */
import type { ReactElement } from 'react';

export interface UnlinkedOAuthAccountProps {
  /** The address the identity service has for this session, when it gave one. */
  readonly email: string | null;
  /** Sign out of this account. */
  readonly onSignOut: () => void;
  /** Sign out and return to the email/password form, which is the owner's own way in. */
  readonly onUseEmailSignIn: () => void;
  readonly busy?: boolean;
}

export function UnlinkedOAuthAccount({
  email, onSignOut, onUseEmailSignIn, busy = false,
}: UnlinkedOAuthAccountProps): ReactElement {
  return (
    <main className="centered card auth" aria-label="This account is not linked">
      <h1>This Google account is not linked to your Brunel owner account.</h1>
      <p className="muted">
        {email === null
          ? 'You are signed in with Google, but this account is not a member of any Brunel organization.'
          : <>You are signed in as <strong>{email}</strong>, and this account is not a member of any Brunel organization.</>}
      </p>
      <p className="muted">
        Brunel has not created anything for it. Your owner account is the one you sign in to with
        your email address and password - sign in that way, and link Google to it from there if you
        want to use this button in future. Creating an organization here would make a second owner
        beside the one you already have.
      </p>
      <div className="row">
        <button type="button" className="primary" disabled={busy} onClick={onUseEmailSignIn}>
          Return to email sign-in
        </button>
        <button type="button" disabled={busy} onClick={onSignOut}>Sign out</button>
      </div>
    </main>
  );
}
